import { createMemo, createSignal, For, Show } from 'solid-js'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Icon from '~/components/Icon'
import type { NodeTypeDTOType, ValidationReportType, WorkflowGraphType, WorkflowNodeExecutionType, WorkflowNodeType } from '~/types/workflow'
import { NODE_WIDTH, WorkflowNodeCard } from './WorkflowNodeCard'

export interface WorkflowCanvasPropsType {
  graph: WorkflowGraphType
  nodeTypes: NodeTypeDTOType[]
  /** Estado de ejecución de cada nodo (por nodeId) si hay una ejecución cargada. */
  nodeExecutions: Map<string, WorkflowNodeExecutionType>
  validation?: ValidationReportType | null
  zoom: number
  onZoom: (zoom: number) => void
  /** Señal de pan: objeto {x,y} mutado desde el canvas vía onPan. */
  pan: { x: number; y: number }
  onPan: (pan: { x: number; y: number }) => void
  selected: string | null
  onSelect: (id: string | null) => void
  selectedEdge: string | null
  onSelectEdge: (id: string | null) => void
  connectFrom: { id: string; port: string } | null
  onConnectFrom: (id: string, port: string) => void
  onConnectComplete: (toId: string) => void
  onUpdateGraph: (mutator: (draft: WorkflowGraphType) => void) => void
  onDuplicateNode: (id: string) => void
  onCopyNode: (id: string) => void
  onToggleDisableNode: (id: string) => void
  onRemoveNode: (id: string) => void
  onOpenConfig: (id: string) => void
  onPointerDownNode: (e: PointerEvent, id: string) => void
  onFit?: () => void
  onAddNode: () => void
}

const MIN_ZOOM = 0.25
const MAX_ZOOM = 2
const CANVAS_W = 4000
const CANVAS_H = 3000

/** Punto de entrada del handle izquierdo del nodo (alineado con WorkflowNodeCard). */
const INPUT_Y = 40

/** Desplazamientos verticales de los handles de salida (mismos que el card). */
const outputOffsets = (count: number, index: number): number => {
  if (count === 0) return 40
  if (count === 1) return 40
  if (count === 2) return index === 0 ? 28 : 78
  return 28 + index * 34
}

/**
 * Canvas del editor: un mundo plano de 4000×3000 con pan (arrastrar el fondo),
 * zoom con rueda y pinch, minimapa y controles de zoom flotantes. Las aristas se
 * dibujan en un SVG sobre el mismo sistema de coordenadas que los nodos.
 */
export function WorkflowCanvas(props: WorkflowCanvasPropsType) {
  let containerRef: HTMLDivElement | undefined
  const [mouse, setMouse] = createSignal<{ x: number; y: number } | null>(null)
  const [dragging, setDragging] = createSignal<{ startX: number; startY: number; panX: number; panY: number } | null>(null)
  const [panning, setPanning] = createSignal(false)

  /** Nodo por id para resolver aristas. */
  const nodeById = createMemo(() => {
    const m = new Map<string, WorkflowNodeType>()
    for (const n of props.graph.nodes) m.set(n.id, n)
    return m
  })

  /** Bounding box de los nodos (para fit y minimapa). */
  const bounds = createMemo(() => {
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity
    for (const n of props.graph.nodes) {
      minX = Math.min(minX, n.position.x)
      minY = Math.min(minY, n.position.y)
      maxX = Math.max(maxX, n.position.x + NODE_WIDTH)
      maxY = Math.max(maxY, n.position.y + 130)
    }
    if (props.graph.nodes.length === 0) return { minX: 0, minY: 0, maxX: NODE_WIDTH, maxY: 130 }
    return { minX, minY, maxX, maxY }
  })

  /** Desplazamiento del viewport en coordenadas de mundo. */
  const worldTopLeft = createMemo(() => ({
    x: -props.pan.x / props.zoom,
    y: -props.pan.y / props.zoom
  }))

  const worldSize = createMemo(() => ({
    w: (containerRef?.clientWidth ?? 800) / props.zoom,
    h: (containerRef?.clientHeight ?? 600) / props.zoom
  }))

  const zoomTo = (next: number, center?: { x: number; y: number }) => {
    const nz = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next))
    if (center && containerRef) {
      const rect = containerRef.getBoundingClientRect()
      const cx = center.x - rect.left
      const cy = center.y - rect.top
      const gx = (cx - props.pan.x) / props.zoom
      const gy = (cy - props.pan.y) / props.zoom
      props.onPan({ x: cx - gx * nz, y: cy - gy * nz })
    }
    props.onZoom(nz)
  }

  const handleWheel = (e: WheelEvent) => {
    if (containerRef) containerRef.style.cursor = e.ctrlKey || e.metaKey ? 'zoom-in' : 'grab'
    const factor = 1.0015 ** -e.deltaY
    zoomTo(props.zoom * factor, { x: e.clientX, y: e.clientY })
  }

  const handlePointerDown = (e: PointerEvent) => {
    // No iniciar pan si el clic fue sobre un nodo o un control.
    const target = e.target as HTMLElement
    if (target.closest('[data-testid="workflow-node"], [data-testid="canvas-controls"], [data-testid="canvas-minimap"]')) return
    setDragging({ startX: e.clientX, startY: e.clientY, panX: props.pan.x, panY: props.pan.y })
    setPanning(false)
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    // Empezar a conectar: al arrastrar el fondo no se selecciona nada todavía.
    if (props.connectFrom) props.onSelectEdge(null)
  }

  const handlePointerMove = (e: PointerEvent) => {
    if (containerRef) {
      const rect = containerRef.getBoundingClientRect()
      const wx = (e.clientX - rect.left - props.pan.x) / props.zoom
      const wy = (e.clientY - rect.top - props.pan.y) / props.zoom
      setMouse({ x: wx, y: wy })
    }
    const d = dragging()
    if (d) {
      const dx = e.clientX - d.startX
      const dy = e.clientY - d.startY
      if (Math.abs(dx) + Math.abs(dy) > 4) setPanning(true)
      if (panning()) {
        props.onPan({ x: d.panX + dx, y: d.panY + dy })
      }
    }
  }

  const handlePointerUp = () => {
    setDragging(null)
    setPanning(false)
    if (containerRef) containerRef.style.cursor = ''
  }

  /** Hacer clic en el fondo: cerrar selección y conexión en curso. */
  const handleBackgroundClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    if (target.closest('[data-testid="workflow-node"], [data-testid="canvas-controls"], [data-testid="canvas-minimap"]')) return
    if (panning()) return
    props.onSelect(null)
    props.onSelectEdge(null)
  }

  /** Posición del handle de salida de un nodo (coordenadas de mundo). */
  const outputPoint = (node: WorkflowNodeType, port: string) => {
    const def = props.nodeTypes.find(t => t.type === node.type)
    const outs = def?.ports.outputs ?? []
    const idx = Math.max(
      0,
      outs.findIndex(p => p.key === port)
    )
    return { x: node.position.x + NODE_WIDTH, y: node.position.y + outputOffsets(outs.length, idx) }
  }

  const inputPoint = (node: WorkflowNodeType) => ({ x: node.position.x, y: node.position.y + INPUT_Y })

  /** Punto del ghost edge: del handle de salida origen al cursor o al destino. */
  const ghostPath = createMemo(() => {
    const conn = props.connectFrom
    if (!conn) return null
    const from = nodeById().get(conn.id)
    if (!from) return null
    const start = outputPoint(from, conn.port)
    let end = mouse()
    // Si hay nodo destino bajo el cursor, alinear al handle de entrada.
    if (end) {
      const t = props.graph.nodes.find(n => {
        const p = n.position
        return end!.x >= p.x && end!.x <= p.x + NODE_WIDTH && end!.y >= p.y && end!.y <= p.y + 130
      })
      if (t) end = inputPoint(t)
    }
    if (!end) end = start
    const dx = Math.max(60, (end.x - start.x) / 2)
    return `M ${start.x} ${start.y} C ${start.x + dx} ${start.y}, ${end.x - dx} ${end.y}, ${end.x} ${end.y}`
  })

  /** Minimapa: escala el mundo al cuadro de 160px. */
  const minimap = createMemo(() => {
    const b = bounds()
    const pad = 16
    const scale = 160 / Math.max(b.maxX - b.minX, 1)
    const nodes = props.graph.nodes.map(n => ({
      x: (n.position.x - b.minX) * scale + pad,
      y: (n.position.y - b.minY) * scale + pad,
      w: NODE_WIDTH * scale,
      h: 130 * scale,
      selected: props.selected === n.id
    }))
    // Rect del viewport
    const tl = worldTopLeft()
    const vs = worldSize()
    const vw = Math.max(8, vs.w * scale)
    const vh = Math.max(8, vs.h * scale)
    const viewport = {
      x: (tl.x - b.minX) * scale + pad,
      y: (tl.y - b.minY) * scale + pad,
      w: vw,
      h: vh
    }
    const boxH = Math.max(b.maxY - b.minY, 1) * scale + pad * 2
    return { nodes, viewport, scale, boxH }
  })

  const minimapClick = (e: MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const b = bounds()
    const pad = 16
    const scale = minimap().scale
    const mx = (e.clientX - rect.left - pad) / scale + b.minX
    const my = (e.clientY - rect.top - pad) / scale + b.minY
    const vw = containerRef?.clientWidth ?? 800
    const vh = containerRef?.clientHeight ?? 600
    props.onPan({ x: -mx * props.zoom + vw / 2, y: -my * props.zoom + vh / 2 })
  }

  const fitToContent = () => {
    const b = bounds()
    const vw = containerRef?.clientWidth ?? 800
    const vh = containerRef?.clientHeight ?? 600
    const margin = 80
    const nz = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.min((vw - margin * 2) / (b.maxX - b.minX), (vh - margin * 2) / (b.maxY - b.minY))))
    props.onZoom(nz)
    props.onPan({
      x: vw / 2 - (b.minX + (b.maxX - b.minX) / 2) * nz,
      y: vh / 2 - (b.minY + (b.maxY - b.minY) / 2) * nz
    })
  }

  return (
    <div
      ref={containerRef}
      data-testid="workflow-canvas"
      class="relative flex-1 touch-none overflow-hidden select-none bg-surface-container [background-image:radial-gradient(theme(colors.border)_1px,transparent_1px)] [background-size:22px_22px]"
      onWheel={handleWheel}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onClick={handleBackgroundClick}
    >
      {/* Capa transformada: pan + zoom */}
      <div class="absolute inset-0 origin-top-left" style={{ transform: `translate(${props.pan.x}px, ${props.pan.y}px) scale(${props.zoom})`, width: `${CANVAS_W}px`, height: `${CANVAS_H}px` }}>
        {/* Aristas */}
        <svg class="absolute inset-0 overflow-visible" width={CANVAS_W} height={CANVAS_H}>
          <For each={props.graph.edges}>
            {e => {
              const from = nodeById().get(e.from)
              const to = nodeById().get(e.to)
              if (!from || !to) return null
              const p1 = outputPoint(from, e.fromPort)
              const p2 = inputPoint(to)
              const dx = Math.max(60, (p2.x - p1.x) / 2)
              const isSelected = props.selectedEdge === e.id
              const isError = e.fromPort === 'error' || e.fromPort === 'false'
              const exec = props.nodeExecutions.get(e.to)
              const color =
                exec?.status === 'failed' || isError
                  ? 'var(--color-red-500)'
                  : exec?.status === 'succeeded'
                    ? 'var(--color-green-500)'
                    : isSelected
                      ? 'var(--color-primary-500)'
                      : 'var(--color-slate-400)'
              const label = e.label
              const midX = (p1.x + p2.x) / 2
              const midY = (p1.y + p2.y) / 2
              return (
                <g>
                  <path
                    data-testid="workflow-edge"
                    data-edge-id={e.id}
                    d={`M ${p1.x} ${p1.y} C ${p1.x + dx} ${p1.y}, ${p2.x - dx} ${p2.y}, ${p2.x} ${p2.y}`}
                    stroke={color}
                    stroke-width={isSelected ? 4 : 2.5}
                    stroke-dasharray={exec?.status === 'running' ? '6 4' : undefined}
                    stroke-linecap="round"
                    fill="none"
                    class="cursor-pointer hover:opacity-80"
                    onClick={ev => {
                      ev.stopPropagation()
                      props.onSelectEdge(isSelected ? null : e.id)
                      props.onSelect(null)
                    }}
                  />
                  {/* Zona de clic ampliada */}
                  <path
                    d={`M ${p1.x} ${p1.y} C ${p1.x + dx} ${p1.y}, ${p2.x - dx} ${p2.y}, ${p2.x} ${p2.y}`}
                    stroke="transparent"
                    stroke-width={16}
                    fill="none"
                    class="cursor-pointer"
                    onClick={ev => {
                      ev.stopPropagation()
                      props.onSelectEdge(isSelected ? null : e.id)
                      props.onSelect(null)
                    }}
                  />
                  <Show when={label}>
                    <g class={isSelected ? 'opacity-100' : 'opacity-70'} pointer-events="none">
                      <rect x={midX - 18} y={midY - 10} width={36} height={20} rx={10} fill="var(--semantic-surface)" stroke={color} stroke-width={1} />
                      <text x={midX} y={midY + 4} text-anchor="middle" font-size="10" font-weight="600" fill={color}>
                        {label}
                      </text>
                    </g>
                  </Show>
                </g>
              )
            }}
          </For>
          {/* Ghost edge mientras se conecta */}
          <Show when={ghostPath()}>
            <path d={ghostPath()!} stroke="var(--color-primary-500)" stroke-width={2.5} stroke-dasharray="6 4" fill="none" pointer-events="none" opacity={0.85} />
          </Show>
          <defs>
            <marker id="workflow-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--color-slate-400)" />
            </marker>
          </defs>
        </svg>

        {/* Nodos */}
        <For each={props.graph.nodes}>
          {n => {
            const def = props.nodeTypes.find(t => t.type === n.type)
            const isConnFrom = props.connectFrom?.id === n.id
            const validationError = props.validation?.errors.find(e => e.nodeId === n.id) ?? props.validation?.warnings.find(e => e.nodeId === n.id) ?? null
            return (
              <WorkflowNodeCard
                node={n}
                def={def}
                selected={props.selected === n.id}
                isConnFrom={isConnFrom}
                connectionPort={props.connectFrom?.port ?? null}
                execution={props.nodeExecutions.get(n.id) ?? null}
                validationError={validationError}
                onSelect={id => {
                  if (props.connectFrom && props.connectFrom.id !== id) props.onConnectComplete(id)
                  else props.onSelect(id)
                }}
                onConnectFrom={props.onConnectFrom}
                onRemove={props.onRemoveNode}
                onDuplicate={props.onDuplicateNode}
                onCopy={props.onCopyNode}
                onToggleDisable={props.onToggleDisableNode}
                onPointerDown={props.onPointerDownNode}
                onOpenConfig={props.onOpenConfig}
              />
            )
          }}
        </For>

        {/* Empty state */}
        <Show when={props.graph.nodes.length === 0}>
          <div class="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <div class="rounded-2xl border border-border bg-surface/95 p-8 text-center shadow-lg">
              <span class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-soft text-primary">
                <Icon icon="material-symbols-light:account-tree-outline" size={30} />
              </span>
              <p class="mt-4 font-semibold text-foreground">Añade un disparador para comenzar</p>
              <p class="mx-auto mt-1 max-w-xs text-sm text-foreground-subtle">Tu flujo empieza con un disparador. Los siguientes nodos se encadenan a partir de él.</p>
              <Button size="sm" color="primary" class="pointer-events-auto mt-4" icon="material-symbols-light:add" onClick={() => props.onAddNode()}>
                Añadir nodo
              </Button>
            </div>
          </div>
        </Show>
      </div>

      {/* Minimapa */}
      <Show when={props.graph.nodes.length > 1}>
        <div
          data-testid="canvas-minimap"
          class="absolute bottom-14 right-3 z-10 hidden rounded-xl border border-border bg-surface/90 p-0 shadow-lg backdrop-blur-sm sm:block"
          style={{ width: '192px', height: `${Math.min(200, minimap().boxH + 8)}px` }}
          onClick={minimapClick}
        >
          <div class="relative h-full w-full" style={{ overflow: 'hidden' }}>
            <For each={minimap().nodes}>
              {nd => (
                <div
                  class="absolute rounded-[2px]"
                  style={{
                    left: `${nd.x}px`,
                    top: `${nd.y}px`,
                    width: `${nd.w}px`,
                    height: `${nd.h}px`,
                    background: nd.selected ? 'var(--color-primary-500)' : 'var(--color-slate-400)',
                    opacity: nd.selected ? 0.9 : 0.5
                  }}
                />
              )}
            </For>
            <div
              class="absolute border-2 border-primary/70"
              style={{ left: `${minimap().viewport.x}px`, top: `${minimap().viewport.y}px`, width: `${minimap().viewport.w}px`, height: `${minimap().viewport.h}px` }}
            />
          </div>
        </div>
      </Show>

      {/* Controles de zoom flotantes */}
      <div data-testid="canvas-controls" class="absolute bottom-3 right-3 z-10 flex items-center gap-0.5 rounded-xl border border-border bg-surface/95 p-1 shadow-lg backdrop-blur-sm">
        <ButtonGroup size="sm" variant="text" color="default">
          <Button icon="material-symbols-light:zoom-out" iconSize={18} aria-label="Alejar" tooltip="Alejar" onClick={() => zoomTo(props.zoom - 0.1)} />
          <button
            type="button"
            class="min-w-12 rounded-md px-1 py-1 text-xs font-medium text-foreground-muted hover:bg-surface-hover"
            onClick={() => {
              props.onZoom(1)
              props.onPan({ x: 0, y: 0 })
            }}
            aria-label="Restablecer zoom"
          >
            {Math.round(props.zoom * 100)}%
          </button>
          <Button icon="material-symbols-light:zoom-in" iconSize={18} aria-label="Acercar" tooltip="Acercar" onClick={() => zoomTo(props.zoom + 0.1)} />
          <Button icon="material-symbols-light:fit-screen" iconSize={18} aria-label="Ajustar vista" tooltip="Ajustar al contenido" onClick={fitToContent} />
        </ButtonGroup>
      </div>
    </div>
  )
}
